import React, { useEffect, useState } from "react";
import { DefaultPopup } from "../../modal";
import { sf_mucphaithu } from "../../../models/response/thu-phi/sf_mucphaithu";
import { hocSinhApi } from "../../../api/hocSinhApi";
import { showNotify } from "../../../helpers/toast";
import { executeWithTimeout } from "../../../helpers/common";
import Button from "../../../components/ui/button";
import { TextArea } from "devextreme-react";
import TextAreaAutoHeight from "../../primer-ui/text-area-auto-height";
import ModalActions from "../../ui/modal/ModalActions";
import { CheckIcon, XIcon } from "@primer/octicons-react";

type SendEmailModalBaseProps = {
    onClose: () => void,
    sf_mucphaithu: sf_mucphaithu,
    ts_hocsinh_id: number,
    buttonText?: string,
    title?: string,
    sendButtonText: string,
    successMessage: string,
    failedMessage: string,
    loadEmailContent: (sf_mucphaithu_id: number) => Promise<any>,
    sendEmail: (payload: { sf_mucphaithu_id: number, email: string, bcc_email: string }) => Promise<any>,
    renderPrintButton?: () => React.ReactNode
}

const SendEmailModalBase: React.FC<SendEmailModalBaseProps> = (props) => {
    const [email_to_send_when_finish, set_email_to_send_when_finish] = useState<string[]>([]);
    const [email_input, set_email_input] = useState<string>("");
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [emailContent, setEmailContent] = useState<string>("Đang tải nội dung email...");

    const parseEmails = (value: string) => {
        return Array.from(
            new Set(
                value
                    .split(";")
                    .map((item) => item.trim())
                    .filter(Boolean)
            )
        );
    };

    const getInvalidEmails = (value: string) => {
        return parseEmails(value).filter((email) => {
            const parts = email.split("@");
            return parts.length !== 2 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
        });
    };

    const invalidEmails = getInvalidEmails(email_input);
    const invalidEmailMessage = invalidEmails.length > 0
        ? `Email không hợp lệ: ${invalidEmails.join("; ")}`
        : "";

    useEffect(() => {
        const handleReloadEmail = async () => {
            const res = await hocSinhApi.selectParentEmail(props.ts_hocsinh_id);
            if (res.is_success) {
                const allEmails = res.data
                    .map((item: any) => item.email)
                    .filter((email: string) => !!email);
                set_email_to_send_when_finish(allEmails);
                set_email_input(allEmails.join("; "));
            }
        };

        handleReloadEmail();
    }, [props.ts_hocsinh_id]);

    useEffect(() => {
        const handleLoadEmailContent = async () => {
            try {
                const res = await props.loadEmailContent(props.sf_mucphaithu.id);
                if (res.is_success && res.data) {
                    setEmailContent(res.data);
                } else {
                    setEmailContent("Không thể tải nội dung email");
                }
            } catch (error) {
                console.error("Error loading email content:", error);
                setEmailContent("Không thể tải nội dung email");
            }
        };

        handleLoadEmailContent();
    }, [props.sf_mucphaithu.id, props.loadEmailContent]);

    const handleSubmit = async () => {
        const parsedEmails = parseEmails(email_input);
        const invalidSubmitEmails = getInvalidEmails(email_input);

        if (invalidSubmitEmails.length > 0) {
            showNotify({
                message: "Vui lòng kiểm tra lại email không hợp lệ trước khi gửi",
                type: "warning"
            });
            return;
        }

        set_email_to_send_when_finish(parsedEmails);
        setIsSaving(true);
        const combinedEmails = parsedEmails.join(",");

        await executeWithTimeout({
            apiCall: () => props.sendEmail({
                sf_mucphaithu_id: props.sf_mucphaithu.id,
                email: combinedEmails,
                bcc_email: ""
            }),
            timeoutMs: 3000,
            onTimeout: () => {
                setIsSaving(false);
                showNotify({
                    message: props.successMessage,
                    type: "success"
                });
                props.onClose();
            },
            onSuccess: (res: any) => {
                setIsSaving(false);
                if (res && res.is_success) {
                    showNotify({
                        message: res.message || props.successMessage,
                        type: "success"
                    });
                    props.onClose();
                } else {
                    showNotify({
                        message: (res && res.message) || props.failedMessage,
                        type: "warning"
                    });
                }
            },
            onError: (err: any) => {
                setIsSaving(false);
                showNotify({
                    message: props.failedMessage,
                    type: "warning"
                });
            }
        });
    };

    return (
        <DefaultPopup title={props.buttonText || props.title || "Gửi thư xác nhận đến PHHS"} width={"60%"} onClose={props.onClose}>
            <div className="pop-up-form">
                <div className="col-md-12 padding-top-1em">
                    <h4>Nhập Email để gửi, phân cách nhau bằng dấu ;</h4>
                    <div className="margin-bottom-5">Gửi tới Email:</div>
                    <br />
                    <TextAreaAutoHeight
                        block
                        value={email_input}
                        placeholder="email1@example.com; email2@example.com"
                        onValueChanged={(value) => {
                            const nextValue = value || "";
                            set_email_input(nextValue);
                            set_email_to_send_when_finish(parseEmails(nextValue));
                        }}
                    />
                    {!!invalidEmailMessage && (
                        <div style={{ color: "red", marginTop: "4px", fontSize: "13px" }}>
                            {invalidEmailMessage}
                        </div>
                    )}
                    <br />
                    <div className="margin-bottom-5">Nội dung email:</div>
                    <br />
                    <TextArea
                        value={emailContent}
                        readOnly={true}
                        stylingMode="outlined"
                        width={"100%"}
                        height={350}
                        showClearButton={false}
                    />
                </div>
            </div>
            <ModalActions>
                <Button variant="default" text="Đóng" leadingVisual={XIcon} onClick={props.onClose} />
                {props.renderPrintButton?.()}
                <Button
                    isLoading={isSaving}
                    variant="primary"
                    leadingVisual={CheckIcon}
                    text={props.sendButtonText}
                    onClick={handleSubmit}
                />
            </ModalActions>
        </DefaultPopup>
    );
};

export default SendEmailModalBase;
